import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, performanceBenefits, CONTACT_INFO } from '../data/mock';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Bike, Gauge, Wrench, Zap, Star } from 'lucide-react';

const benefitIcons = [Gauge, Wrench, Bike, Star];

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'kids-aluminum-bike') || products[0];
  const gridSlugs = ['smart-cycling-helmet','wireless-bike-computer','bicycle-lock-with-alarm','bluetooth-bike-speaker','front-bike-basket','electric-bike-conversion-kit','hitch-mount-bike-rack','teen-bicycle'];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);

  useEffect(() => { document.title = 'Trailside Cycling | Bicycle Parts, Accessories & Supplies'; }, []);

  return (
    <div className="min-h-screen bg-white text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Hero — split, light, image right */}
        <section className="bg-dark-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-athletic-100 text-athletic-700 text-xs font-bold uppercase tracking-[0.18em] rounded-full mb-6"><Zap className="w-3.5 h-3.5" /> Gear up. Ride farther.</span>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-dark-900 leading-[1.02]">Everything your <span className="text-athletic-600">bike</span> needs, in one shop</h1>
              <p className="mt-5 text-lg text-dark-500 max-w-xl">From safety and tools to ride tech and complete bikes, Trailside Cycling stocks rider-tested parts and accessories &mdash; with free USA shipping on every order.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-athletic-600 px-8 py-4 font-semibold text-white transition hover:bg-athletic-700 shadow-button">Shop All Gear <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/shop?category=bikes-big-gear" className="inline-flex items-center gap-2 rounded-full border border-dark-300 px-8 py-4 font-semibold text-dark-800 hover:border-athletic-500 hover:text-athletic-600 transition">Browse Bikes</Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-dark-500">
                <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-athletic-600" /> Free USA shipping</span>
                <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-athletic-600" /> 30-day returns</span>
                <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-athletic-600" /> Secure checkout</span>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-elevated aspect-[4/5]">
                <img src={siteImages.heroMain} alt="Cyclist riding" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-elevated p-4 flex items-center gap-3 hidden sm:flex">
                <div className="w-12 h-12 rounded-full bg-power-100 flex items-center justify-center text-power-600"><Bike className="w-6 h-6" /></div>
                <div><p className="font-display text-2xl font-bold text-dark-900 leading-none">14+</p><p className="text-xs text-dark-500">rider-tested products</p></div>
              </div>
            </div>
          </div>
        </section>

        {/* Category cards */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">Shop by category</span><h2 className="font-display text-4xl font-bold text-dark-900">Find your kit</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-athletic-700 hover:gap-2.5 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
              {categories.map((category) => (
                <Link key={category.id} to={`/shop?category=${category.slug}`} className="group relative h-56 rounded-2xl overflow-hidden bg-dark-100">
                  <img src={category.image} alt={category.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-950/85 via-dark-950/25 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <h3 className="font-display text-lg font-bold text-white leading-tight">{category.name}</h3>
                    <p className="text-xs text-power-300 mt-0.5 font-semibold">{category.productCount} product{category.productCount > 1 ? 's' : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Product grid */}
        <section className="bg-dark-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10"><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">Trending now</span><h2 className="font-display text-4xl font-bold text-dark-900">Popular with riders</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{productsGrid.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
            <div className="text-center mt-12"><Link to="/shop" className="inline-flex items-center gap-2 px-8 py-3.5 bg-athletic-600 text-white font-semibold rounded-full hover:bg-athletic-700 transition">Browse all gear <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>

        {/* Benefits band (blue) */}
        <section className="bg-gradient-athletic py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {performanceBenefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={b.id} className="text-center sm:text-left">
                    <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center text-white mb-4 mx-auto sm:mx-0"><Icon className="w-6 h-6" /></div>
                    <h3 className="font-display text-xl font-bold text-white">{b.title}</h3>
                    <p className="text-white/80 text-sm mt-1">{b.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Flagship promo split (image left) */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl bg-dark-50 p-8"><img src={featuredProduct.image} alt={featuredProduct.name} className="w-full max-w-md mx-auto object-contain" /></div>
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-3">Flagship pick</span>
              <h2 className="font-display text-4xl font-bold text-dark-900 mb-4">{featuredProduct.name}</h2>
              <p className="text-dark-500 mb-5 max-w-lg">{featuredProduct.description.slice(0, 180)}&hellip;</p>
              <p className="text-3xl font-bold text-athletic-600 mb-7">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-athletic-600 text-white rounded-full font-semibold hover:bg-athletic-700 transition">Buy now</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border border-dark-300 text-dark-800 rounded-full font-medium hover:border-athletic-500 hover:text-athletic-600 transition">Browse more</Link>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-dark-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">How it works</span><h2 className="font-display text-4xl font-bold text-dark-900">From cart to kickstand</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="bg-white border border-dark-100 rounded-2xl p-6 shadow-soft"><span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-athletic-600 text-white font-display font-bold">{step.id}</span><h3 className="font-display text-xl font-bold text-dark-900 mt-4">{step.title}</h3><p className="text-dark-500 text-sm mt-2">{step.description}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-4xl font-bold text-dark-900 text-center mb-10">Frequently asked questions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'What is the typical shipping time?', a: `Orders ship via USPS within 1 business day and usually arrive in 2 to 4 business days. For delays, reach us at ${CONTACT_INFO.phone}.` },
                { q: 'Is shipping free?', a: 'Yes — every order ships free via USPS Standard Shipping within the USA, no minimum required.' },
                { q: 'How do returns work?', a: 'You have 30 days from delivery to start a return. Reach out and we will guide you through it.' },
                { q: 'How do I reach support?', a: `Our crew is available at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (<div key={i} className="bg-dark-50 rounded-2xl p-6 border border-dark-100"><h3 className="font-display text-xl font-bold text-dark-900">{faq.q}</h3><p className="mt-2 text-dark-600">{faq.a}</p></div>))}
            </div>
            <div className="text-center mt-10"><Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold">See all FAQs <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
